import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, PageLoader, EmptyState } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RiskBadge } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { EvidenceBadge } from '@/components/common/EvidenceBadge';
import { SimulationDisclaimer } from '@/components/common/SimulationDisclaimer';
import { VerifiedBadge } from '@/components/common/VerifiedBadge';
import { api } from '@/lib/api';
import { fmtRel } from '@/lib/format';
import { fmtDistance } from '@/lib/format2';
import usePageTitle from '@/hooks/usePageTitle';

const STATUSES = ['All', 'New', 'Under review', 'Validated', 'Escalated', 'Converted to incident', 'Closed'];

export default function AlertsPage() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState('All');
  usePageTitle('Alerts');

  useEffect(() => {
    const params = { limit: 300 };
    if (status !== 'All') params.status = status;
    api.get('/alerts', { params }).then(r => setItems(r.data));
  }, [status]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader title="Alerts" subtitle="Threshold-driven alerts from analyst-validated AI observations. All alerts require appropriate operational verification before action." />
      <Card className="p-3 mb-4 flex gap-2 flex-wrap" data-testid="alerts-filter-row">
        {STATUSES.map(s => (
          <button key={s} onClick={() => setStatus(s)}
            data-testid={`alerts-filter-${s.replace(/ /g, '-')}`}
            className={`text-xs rounded-md px-2 py-1 border ${status === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary border-border hover:bg-secondary/70'}`}>{s}</button>
        ))}
      </Card>
      {!items ? <PageLoader /> : items.length === 0 ? <EmptyState title="No active alerts for the selected filter." hint="Try a different status filter or wait for the next observation cycle." /> : (
        <Card className="p-0 divide-y divide-border" data-testid="alerts-table">
          {items.map(a => (
            <Link key={a.id} to={`/app/alerts/${a.id}`}
                  className={`flex items-center gap-3 p-3 hover:bg-secondary/60 risk-stripe risk-stripe-${a.severity?.toLowerCase()}`}
                  data-testid={`alert-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {a.title}
                  {a.is_simulation ? <SimulationDisclaimer compact /> : <VerifiedBadge />}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span>{fmtRel(a.created_at)}</span>
                  <span>·</span>
                  <span>{a.status}</span>
                  {a.distance_to_asset_m != null && (
                    <>
                      <span>·</span>
                      <span className="font-mono">{fmtDistance(a.distance_to_asset_m)} from asset</span>
                    </>
                  )}
                </div>
              </div>
              <CategoryBadge category={a.category} />
              {a.evidence_quality && <EvidenceBadge quality={a.evidence_quality} />}
              <Badge variant="outline" className="text-[10px] font-mono">risk {a.risk_score}</Badge>
              <RiskBadge severity={a.severity} testId="alert-risk-badge" />
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}
